import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminAPI, usersAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  MoreVertical, Shield, Ban, CheckCircle, Crown, Users, ClipboardList,
  Package, BarChart3, Search, Calendar, User as UserIcon, Archive
} from 'lucide-react';

const AdminPage = () => {
  const { user: currentUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Task filters
  const [taskSearch, setTaskSearch] = useState('');
  const [taskStatus, setTaskStatus] = useState('');
  const [taskTeam, setTaskTeam] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [statsRes, usersRes, teamsRes, tasksRes] = await Promise.all([
        adminAPI.stats(),
        usersAPI.list(),
        adminAPI.teams(),
        adminAPI.tasks({})
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setTeams(teamsRes.data);
      setTasks(tasksRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const params = {};
      if (taskSearch) params.search = taskSearch;
      if (taskStatus) params.status = taskStatus;
      if (taskTeam) params.group_id = taskTeam;
      const res = await adminAPI.tasks(params);
      setTasks(res.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    }
  };

  useEffect(() => {
    if (!loading) fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskStatus, taskTeam]);

  const handleToggleBan = async (userId, currentStatus) => {
    try {
      await usersAPI.toggleBan(userId);
      toast.success(currentStatus ? 'User unbanned' : 'User banned');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await usersAPI.changeRole(userId, newRole);
      toast.success('Role updated');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';

  const statusColors = {
    OPEN: 'bg-blue-100 text-blue-800',
    IN_PROGRESS: 'bg-amber-100 text-amber-800',
    WAITING_ON_PARTS: 'bg-purple-100 text-purple-800',
    BLOCKED: 'bg-red-100 text-red-800',
    DONE: 'bg-emerald-100 text-emerald-800'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-heading">Admin Dashboard</h1>
        <p className="text-slate-600">System-wide overview and management</p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview" data-testid="admin-tab-overview">
            <BarChart3 className="h-4 w-4 mr-2" />Overview
          </TabsTrigger>
          <TabsTrigger value="tasks" data-testid="admin-tab-tasks">
            <ClipboardList className="h-4 w-4 mr-2" />All Tasks
          </TabsTrigger>
          <TabsTrigger value="teams" data-testid="admin-tab-teams">
            <Users className="h-4 w-4 mr-2" />All Teams
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">
            <UserIcon className="h-4 w-4 mr-2" />All Users
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {stats && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600">Users</p>
                        <p className="text-2xl font-bold mt-1">{stats.users.total}</p>
                      </div>
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <UserIcon className="h-5 w-5 text-indigo-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600">Teams</p>
                        <p className="text-2xl font-bold mt-1">{stats.teams.total}</p>
                      </div>
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Users className="h-5 w-5 text-purple-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600">Tasks</p>
                        <p className="text-2xl font-bold mt-1">{stats.tasks.total}</p>
                      </div>
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <ClipboardList className="h-5 w-5 text-amber-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600">Orders</p>
                        <p className="text-2xl font-bold mt-1">{stats.orders.total}</p>
                      </div>
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <Package className="h-5 w-5 text-emerald-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Task Status Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Task Status Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                    {[
                      { label: 'Open', value: stats.tasks.open, color: 'bg-blue-500' },
                      { label: 'In Progress', value: stats.tasks.in_progress, color: 'bg-amber-500' },
                      { label: 'Waiting on Parts', value: stats.tasks.waiting_on_parts, color: 'bg-purple-500' },
                      { label: 'Blocked', value: stats.tasks.blocked, color: 'bg-red-500' },
                      { label: 'Done', value: stats.tasks.done, color: 'bg-emerald-500' },
                      { label: 'Archived', value: stats.tasks.archived, color: 'bg-slate-400' },
                    ].map(item => (
                      <div key={item.label} className="text-center p-4 rounded-lg bg-slate-50">
                        <div className={`w-3 h-3 rounded-full ${item.color} mx-auto mb-2`} />
                        <p className="text-2xl font-bold">{item.value}</p>
                        <p className="text-xs text-slate-500 mt-1">{item.label}</p>
                      </div>
                    ))}
                  </div>
                  {stats.tasks.total > 0 && (
                    <div className="flex h-3 rounded-full overflow-hidden mt-4 bg-slate-100">
                      {stats.tasks.open > 0 && <div className="bg-blue-500" style={{ width: `${(stats.tasks.open / stats.tasks.total) * 100}%` }} />}
                      {stats.tasks.in_progress > 0 && <div className="bg-amber-500" style={{ width: `${(stats.tasks.in_progress / stats.tasks.total) * 100}%` }} />}
                      {stats.tasks.waiting_on_parts > 0 && <div className="bg-purple-500" style={{ width: `${(stats.tasks.waiting_on_parts / stats.tasks.total) * 100}%` }} />}
                      {stats.tasks.blocked > 0 && <div className="bg-red-500" style={{ width: `${(stats.tasks.blocked / stats.tasks.total) * 100}%` }} />}
                      {stats.tasks.done > 0 && <div className="bg-emerald-500" style={{ width: `${(stats.tasks.done / stats.tasks.total) * 100}%` }} />}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Teams Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Tasks by Team</CardTitle>
                </CardHeader>
                <CardContent>
                  {teams.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No teams yet</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Team</TableHead>
                          <TableHead className="text-center">Members</TableHead>
                          <TableHead className="text-center">Tasks</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {teams.map(team => (
                          <TableRow key={team.group_id}>
                            <TableCell className="font-medium">{team.name}</TableCell>
                            <TableCell className="text-center">{team.member_count}</TableCell>
                            <TableCell className="text-center">{team.task_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* User Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <UserIcon className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Active Users</p>
                      <p className="text-xl font-bold">{stats.users.total - stats.users.banned}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Crown className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Admins</p>
                      <p className="text-xl font-bold">{stats.users.admins}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                      <Ban className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Banned</p>
                      <p className="text-xl font-bold">{stats.users.banned}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* All Tasks Tab */}
        <TabsContent value="tasks" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search tasks..."
                value={taskSearch}
                onChange={(e) => setTaskSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchTasks()}
                className="pl-10"
                data-testid="admin-task-search"
              />
            </div>
            <Select value={taskStatus || "all"} onValueChange={(v) => setTaskStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="OPEN">Open</SelectItem>
                <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                <SelectItem value="WAITING_ON_PARTS">Waiting on Parts</SelectItem>
                <SelectItem value="BLOCKED">Blocked</SelectItem>
                <SelectItem value="DONE">Done</SelectItem>
              </SelectContent>
            </Select>
            <Select value={taskTeam || "all"} onValueChange={(v) => setTaskTeam(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                {teams.map(t => (
                  <SelectItem key={t.group_id} value={t.group_id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Team</TableHead>
                    <TableHead>Assignee</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Labels</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tasks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No tasks found
                      </TableCell>
                    </TableRow>
                  ) : (
                    tasks.map(task => (
                      <TableRow key={task.task_id}>
                        <TableCell>
                          <p className="font-medium text-sm">{task.title}</p>
                          {task.description && (
                            <p className="text-xs text-slate-500 truncate max-w-xs">{task.description}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-600">{task.team_name || '—'}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-600">{task.assignee_name || '—'}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[task.status]}>
                            {task.status === 'WAITING_ON_PARTS' ? 'WAITING ON PARTS' : task.status.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {task.due_date ? (
                            <span className="text-sm text-slate-600 flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {format(new Date(task.due_date), 'MMM d')}
                            </span>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {task.label_details?.map(l => (
                              <Badge
                                key={l.label_id}
                                style={{ backgroundColor: l.color + '20', color: l.color }}
                                className="text-xs"
                              >
                                {l.name}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <p className="text-sm text-slate-500">{tasks.length} tasks total</p>
        </TabsContent>

        {/* All Teams Tab */}
        <TabsContent value="teams" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">All Teams</CardTitle>
              <CardDescription>{teams.length} teams in the system</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Team Name</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead className="text-center">Members</TableHead>
                    <TableHead className="text-center">Tasks</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teams.map(team => {
                    const owner = team.members?.find(m => m.role === 'owner');
                    return (
                      <TableRow key={team.group_id}>
                        <TableCell className="font-medium">{team.name}</TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {owner?.user_id || '—'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{team.member_count}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{team.task_count}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {team.created_at ? format(new Date(team.created_at), 'MMM d, yyyy') : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">All Users</CardTitle>
              <CardDescription>Manage user accounts, roles, and access</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.user_id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.picture} />
                            <AvatarFallback className="bg-indigo-100 text-indigo-600 text-sm">
                              {getInitials(user.name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{user.name}</span>
                          {user.user_id === currentUser?.user_id && (
                            <Badge variant="outline" className="text-xs">You</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{user.email}</TableCell>
                      <TableCell>
                        <Badge className={user.role === 'admin' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-800'}>
                          {user.role === 'admin' && <Crown className="h-3 w-3 mr-1" />}
                          {user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.banned ? (
                          <Badge variant="destructive">
                            <Ban className="h-3 w-3 mr-1" />Banned
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-800">
                            <CheckCircle className="h-3 w-3 mr-1" />Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : 'N/A'}
                      </TableCell>
                      <TableCell className="text-right">
                        {user.user_id !== currentUser?.user_id && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`user-actions-${user.user_id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleChangeRole(user.user_id, user.role === 'admin' ? 'user' : 'admin')}
                              >
                                <Shield className="mr-2 h-4 w-4" />
                                {user.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleToggleBan(user.user_id, user.banned)}
                                className={user.banned ? 'text-emerald-600' : 'text-red-600'}
                              >
                                {user.banned ? (
                                  <><CheckCircle className="mr-2 h-4 w-4" />Unban User</>
                                ) : (
                                  <><Ban className="mr-2 h-4 w-4" />Ban User</>
                                )}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPage;
